// Kinhall: the trainers' hall where the roads of the three realms meet
// (client/kinhall.ts draws it, server/kinhall.ts keeps who is there).
// Players walk on its floor and use its stations; the team keeps hunting
// meanwhile, so being here never pauses anything.
import { HALL_DECOR, HALL_LANTERNS } from '../client/kinhall-art.ts';

/** The scene in tiles (24 map px each). The camera follows you across it. */
export const HALL_W = 48;
export const HALL_H = 27;

/** Where trainers may stand: the paved floor, in tile units. */
export const FLOOR = { x0: 3, x1: 45, y0: 6.8, y1: 25.6 } as const;

/** Where a trainer appears on entering: inside the Road gate, with a little spread so arrivals don't stack. */
export const SPAWN = { x: 24, y: 23.4, spread: 3 } as const;

/** At most this many other trainers are sent to each visitor (the most recent arrivals). */
export const HALL_SHOWN = 60;

/** The Hall of Legends: plinths along the back wall, each showing the server's best at something.
 *  The latest tournament champion stands in the middle, where the runner from the Well ends. */
export type DisplayId = 'aura' | 'best' | 'tour' | 'top' | 'arena';
export type StationId = 'well' | 'vault' | 'altar' | 'hatchery' | 'merchant' | 'board' | 'quests' | 'gate' | DisplayId;

export interface Station {
  id: StationId;
  name: string;
  /** The prop's foot point, in tiles. */
  x: number; y: number;
  /** Where a trainer stands to use it. */
  stand: { x: number; y: number };
}

export const STATIONS: Station[] = [
  { id: 'well', name: "Urd's Well", x: 24, y: 15.3, stand: { x: 24, y: 17.6 } },
  { id: 'vault', name: 'Kin Vault', x: 8, y: 13, stand: { x: 8, y: 14.4 } },
  { id: 'altar', name: 'Altar', x: 11, y: 6.6, stand: { x: 11, y: 8 } },
  { id: 'aura', name: 'Latest Aura', x: 18, y: 6.4, stand: { x: 18, y: 8 } },
  { id: 'best', name: 'Best Catch', x: 21, y: 6.4, stand: { x: 21, y: 8 } },
  { id: 'tour', name: 'Tournament Champion', x: 24, y: 6.4, stand: { x: 24, y: 8 } },
  { id: 'top', name: 'Top Trainer', x: 27, y: 6.4, stand: { x: 27, y: 8 } },
  { id: 'arena', name: 'Arena Champion', x: 30, y: 6.4, stand: { x: 30, y: 8 } },
  { id: 'hatchery', name: 'Hatchery', x: 37, y: 6.6, stand: { x: 37, y: 8 } },
  { id: 'merchant', name: 'Merchant', x: 40, y: 13, stand: { x: 40, y: 14.4 } },
  { id: 'board', name: 'Trade board', x: 39.5, y: 21, stand: { x: 39.5, y: 22.2 } },
  { id: 'quests', name: 'Quest board', x: 8.5, y: 21, stand: { x: 8.5, y: 22.2 } },
  { id: 'gate', name: 'Road gate', x: 24, y: 26.6, stand: { x: 24, y: 25.4 } },
];
export const STATION: Record<StationId, Station> = Object.fromEntries(STATIONS.map((s) => [s.id, s])) as Record<StationId, Station>;
export const DISPLAYS: DisplayId[] = ['aura', 'best', 'tour', 'top', 'arena'];

/** Urd's Well basin: an ellipse `SQUASH` times wider than tall (tiles). */
export const WELL = { x: 24, y: 15, r: 2.4 } as const;
export const SQUASH = 1.7;

/** Each piece of furniture's base on the floor (client/kinhall-art.ts HALL_DECOR), measured from its art: half its width
 *  in tiles, and how much wider than deep it is. Anything art adds later without an entry here blocks as `DECOR_FOOT_DEFAULT`. */
export const DECOR_FOOT: Record<string, { r: number; squash: number }> = {
  'prop-hall-bench-midgard': { r: 1.05, squash: 3 },
  'prop-hall-bench-hellas': { r: 1.05, squash: 3 },
  'prop-hall-brazier': { r: 0.6, squash: 1.5 },
  'prop-hall-planter': { r: 0.6, squash: 1.5 },
  'prop-hall-planter-yomi': { r: 0.65, squash: 1.6 },
  'prop-midgard-runestone': { r: 0.6, squash: 1.7 },
  'prop-midgard-snowbush': { r: 0.5, squash: 1.7 },
  'prop-hellas-amphora': { r: 0.4, squash: 1.3 },
  'prop-yomi-lantern': { r: 0.4, squash: 1.4 },
};
export const DECOR_FOOT_DEFAULT = { r: 0.5, squash: 1.6 } as const;

/** Ground nobody stands on: the Well's basin, each station's prop, and the furniture and stone lanterns (ellipses, r across, r / squash deep). */
export const BLOCKERS: { x: number; y: number; r: number; squash: number }[] = [
  { ...WELL, squash: SQUASH },
  ...STATIONS.filter((s) => s.id !== 'well' && s.id !== 'gate').map((s) => ({ x: s.x, y: s.y - 0.35, r: s.id === 'merchant' ? 1.4 : 1.1, squash: 2.4 })),
  ...[...HALL_LANTERNS.map(([x, y]): [string, number, number] => ['prop-yomi-lantern', x, y]), ...HALL_DECOR].map(([id, x, y]) => ({ x, y: y - 0.05, ...(DECOR_FOOT[id] ?? DECOR_FOOT_DEFAULT) })),
];

/** The point on a blocker's rim nearest to (x, y) when (x, y) is inside it, else null. */
export function insideBlocker(x: number, y: number, pad = 0): { x: number; y: number } | null {
  for (const b of BLOCKERS) {
    const dx = x - b.x, dy = (y - b.y) * b.squash, d = Math.hypot(dx, dy), r = b.r + pad;
    if (d >= r) continue;
    if (!d) return { x: b.x, y: b.y + r / b.squash };
    return { x: b.x + (dx / d) * r, y: b.y + (dy / d) * r / b.squash };
  }
  return null;
}

/** A spot on the floor nearest to (x, y), outside every blocker. */
export function onFloor(x: number, y: number): { x: number; y: number } {
  const c = (v: number, a: number, b: number) => Math.min(b, Math.max(a, Number.isFinite(v) ? v : a));
  let px = c(x, FLOOR.x0, FLOOR.x1), py = c(y, FLOOR.y0, FLOOR.y1);
  for (let i = 0; i < 3; i++) {
    const out = insideBlocker(px, py, 0.1);
    if (!out) break;
    px = c(out.x, FLOOR.x0, FLOOR.x1); py = c(out.y, FLOOR.y0, FLOOR.y1);
  }
  return { x: Math.round(px * 100) / 100, y: Math.round(py * 100) / 100 };
}

// ---------------- emotes ----------------
// A trainer in the hall plays an emote: an icon pops over their head and they
// move with it. A few are free; the rest are diamond cosmetics, account-bound.
// Earned emotes (ranked season milestones, shared/ranked.ts) are never sold: they
// show only once owned.
export type EmoteMotion = 'none' | 'hop' | 'dance' | 'bow' | 'burst';
export interface Emote {
  id: string; name: string; /** 0 = free for everyone (unless earned). */ price: number; motion: EmoteMotion;
  /** Earned through play, never sold: owned only once granted (TrainerDTO.emotes). */
  earned?: 'ranked';
}

export const EMOTE_PRICE = 15;
export const EMOTES: Emote[] = [
  { id: 'wave', name: 'Wave', price: 0, motion: 'none' },
  { id: 'cheer', name: 'Cheer', price: 0, motion: 'hop' },
  { id: 'heart', name: 'Heart', price: 0, motion: 'none' },
  { id: 'huh', name: 'Huh?', price: 0, motion: 'none' },
  { id: 'dance', name: 'Dance', price: EMOTE_PRICE, motion: 'dance' },
  { id: 'bow', name: 'Bow', price: EMOTE_PRICE, motion: 'bow' },
  { id: 'fireworks', name: 'Fireworks', price: EMOTE_PRICE, motion: 'burst' },
  // Ranked season 1, Platinum (shared/ranked.ts RANKED_ART).
  { id: 'ranked-salute', name: 'Salute', price: 0, motion: 'bow', earned: 'ranked' },
];
export const EMOTE: Record<string, Emote> = Object.fromEntries(EMOTES.map((e) => [e.id, e]));
/** How long an emote shows, and the shortest gap between two from one trainer. */
export const EMOTE_MS = 2600;
export const EMOTE_GAP_MS = 1500;

export function ownsEmote(t: { emotes?: string[] }, id: string): boolean {
  const e = EMOTE[id];
  return !!e && ((e.price === 0 && !e.earned) || !!t.emotes?.includes(id));
}

/** The emotes a trainer's emote bar shows, in order: every free and diamond one, and earned ones once owned. */
export function emoteBar(t: { emotes?: string[] }): Emote[] {
  return EMOTES.filter((e) => !e.earned || ownsEmote(t, e.id));
}
